import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IEventFeedResponse } from "@finbackoffice/websocket-client";
import Link from "next/link";
import { FavoriteEventsContext, useRuntimeConfig } from "@finbackoffice/site-core";
import classNames from "classnames";
import { formatAppNameText } from "@finbackoffice/fe-core";
import { Svg } from "components/base/svg/Svg";
import { useCalendar, useDate } from "hooks";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import SportsLayout from "components/layout/sportsbook/SportsLayout";
import { RouteNames } from "utils/constants";
import { convertGamesToSports } from "utils/helpers";
import styles from "./calendar.module.sass";

export interface ICalendarDateState {
    from: number;
    to: number;
}

type IProps = {
    date: {
        from: number;
        to: number;
    };
};

const Calendar: FC<IProps> = ({ date }) => {
    const { games } = useCalendar(date);
    const { formatDate } = useDate();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { isFav, removeFav, addFav } = useContext(FavoriteEventsContext);
    const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
    const selectedSportGames = useMemo(
        () => games.filter((game) => game.sport_id === selectedSportId),
        [games, selectedSportId],
    );

    const onSportItemClickHandler = useCallback((sportId: number) => {
        setSelectedSportId(sportId);
    }, []);

    const sports = useMemo(() => convertGamesToSports(games), [games]);

    useEffect(() => {
        if (games && games.length) {
            const selectedSportDoesNotExist =
                selectedSportId && sports.findIndex((sport) => sport.id === selectedSportId) === -1;

            if (sports.length && (!selectedSportId || selectedSportDoesNotExist)) {
                setSelectedSportId(sports[0].id);
            }
        } else {
            setSelectedSportId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sports]);

    const handleFavClick = useCallback(
        (game: IEventFeedResponse) => {
            if (isFav(game.id)) {
                removeFav(game.id);
            } else {
                addFav({
                    id: game.id,
                    timestamp: game.scheduled_at ? new Date(game.scheduled_at).valueOf() : 0,
                    sport_id: game.sport_id,
                    tournament_id: game.tournament_id,
                });
            }
        },
        [addFav, isFav, removeFav],
    );

    const renderGame = useCallback(
        (game: IEventFeedResponse) => (
            <Link
                key={game.id}
                href={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${game.sport_slug}/${game.category_id}/${game.tournament_id}/${game.id}`}
                className={styles.calendarItem}>
                <span>
                    <Svg
                        src={`/${formatAppNameText(COMMON_SITE_CONFIGS.appName)}/mobile/${
                            isFav(game.id) ? "star" : "live-game-favorite-unstarred"
                        }.svg`}
                        wrapper="span"
                        className={classNames(styles.favorite, {
                            [styles.favoriteStarred]: isFav(game.id),
                        })}
                        onClick={(event) => {
                            event.preventDefault();
                            handleFavClick(game);
                        }}
                    />
                </span>
                <div>
                    <span>
                        {game.home_team?.name}-{game.away_team?.name}
                    </span>
                    <span>
                        <Svg
                            src={`/common/regions/${game.category_id}.svg`}
                            wrapper="span"
                            className="svg-region-icon"
                            loader
                        />
                        <span
                            className={
                                styles.name
                            }>{`${game.category_name}, ${game.tournament_name}`}</span>
                        {game.scheduled_at && (
                            <span className={styles.time}>
                                {formatDate(new Date(game.scheduled_at), "HH:mm")}
                            </span>
                        )}
                    </span>
                </div>
            </Link>
        ),
        [formatDate, handleFavClick, isFav, COMMON_SITE_CONFIGS.appName],
    );

    return (
        <ErrorBoundary name={SportsLayout.name}>
            <SportsLayout
                sports={sports}
                activeSportId={selectedSportId ?? undefined}
                onSportClick={onSportItemClickHandler}>
                {selectedSportGames.map((game) => renderGame(game))}
            </SportsLayout>
        </ErrorBoundary>
    );
};

export default Calendar;
